import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BookingReviewsTab } from "../../../components/userDetail/BookingReviewsTab";
import { BookingTab } from "../../../components/userDetail/Bookingtab";
import { RideReviewsTab } from "../../../components/userDetail/RideReviewsTab";
import { RidesTab } from "../../../components/userDetail/RideTab";
import { UserDetailTabs } from "../../../components/userDetail/UserDetailTabs";
import { BalanceTab } from "../../../components/userDetail/BalanceTab";
import { useUserData } from "../../../hooks/useUserData";
import { getPeriod, isEmpty, isNotEmpty } from "../../../helpers/general";
import { Periods } from "../../../helpers/enums";
import GeneralInfoForm from "../../Drivers/GeneralInfoForm";
import BlockUserModal from "../../../components/modals/BlockUserModal";
import apiService from "../../../service/apiService";
import { useCookie } from "../../../hooks/useCookie";
import PromptModal from "../../../components/modals/PromptModal";
import { UserPointsHistoryTab } from "../../../components/userDetail/bonusTab";
import {
  PartnerDetailContent,
  PartnerDetailNav,
} from "../../../components/userDetail/PartnerDetailTabs";
import UserDetailHeader from "../../../components/userDetail/UserDetailHeader";
import AppConfigurationTab from "../../Drivers/AppConfiguration";

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = parseInt(searchParams.get("tab") || "0", 10);
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [action, setAction] = useState("");
  const { token } = useCookie("vToken");
  const [loading, setLoading] = useState(false);

  const [rideFilters, setRideFilters] = useState({
    userId: id,
    status: "",
    page: 1,
    limit: 10,
    startDate: "",
    endDate: "",
  });

  const [rideReviewsFilters, setRideReviewsFilters] = useState({
    page: 1,
    limit: 10,
  });

  const [BookingReviewsFilters, setBookingReviewsFilters] = useState({
    page: 1,
    limit: 10,
  });

  const [pointsHistoryFilters, setPointsHistoryFilters] = useState({
    page: 1,
    limit: 10,
    type: "",
  });

  const handleResetFilters = () => {
    setRideFilters({
      userId: id,
      status: "",
      page: 1,
      limit: 10,
      startDate: "",
      endDate: "",
    });

    setRideReviewsFilters({
      page: 1,
      limit: 10,
    });

    setBookingReviewsFilters({
      page: 1,
      limit: 10,
    });
  };

  const handleSetActiveTab = (tabIndex: number) => {
    setActiveTab(tabIndex);
    handleResetFilters();

    navigate(`${location.pathname}?tab=${tabIndex}`, { replace: true });
  };

  useEffect(() => {
    // setActiveTab(tabFromUrl);
    // Restore ride filters
    if (activeTab === 1) {
      setRideFilters((prev) => ({
        ...prev,
        status: searchParams.get("status") || prev.status,
        page: parseInt(searchParams.get("page") || `${prev.page}`, 10),
        startDate: searchParams.get("startDate") || prev.startDate,
        endDate: searchParams.get("endDate") || prev.endDate,
      }));
    } else if (activeTab === 3) {
      setBookingReviewsFilters((prev) => ({
        ...prev,
        page: parseInt(searchParams.get("b_reviewPage") || `${prev.page}`, 10),
      }));
    } else if (activeTab === 4) {
      setRideReviewsFilters((prev) => ({
        ...prev,
        page: parseInt(searchParams.get("r_reviewPage") || `${prev.page}`, 10),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set("tab", String(activeTab));

    // Only include filters relevant to the current tab
    if (activeTab === 1) {
      if (rideFilters.status) newSearchParams.set("status", rideFilters.status);
      if (rideFilters.page !== 1)
        newSearchParams.set("page", String(rideFilters.page));
      if (rideFilters.startDate)
        newSearchParams.set("startDate", rideFilters.startDate);
      if (rideFilters.endDate)
        newSearchParams.set("endDate", rideFilters.endDate);
    } else if (activeTab === 3) {
      if (BookingReviewsFilters.page !== 1)
        newSearchParams.set("b_reviewPage", String(BookingReviewsFilters.page));
    } else if (activeTab === 4) {
      if (rideReviewsFilters.page !== 1)
        newSearchParams.set("r_reviewPage", String(rideReviewsFilters.page));
    }

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  }, [
    activeTab,
    rideFilters,
    BookingReviewsFilters,
    rideReviewsFilters,
    navigate,
    location.pathname,
  ]);

  const {
    userBookingReviewsData,
    rides,
    isLoading,
    UserRidesreviewsData,
    deleteReview,
    isDeletingReview,
    deleteRideReview,
    isDeletingRideReview,
    userData,
    invalidateQueryUserInfoQuery,
    userPointsHistoryData,
  } = useUserData(
    id!,
    rideFilters,
    rideReviewsFilters,
    BookingReviewsFilters,
    pointsHistoryFilters,
  );

  const isPartner = userData?.role?.label === "Partner";

  const period: Periods | undefined = useMemo(() => {
    if (rideFilters.startDate && rideFilters.endDate) {
      return getPeriod(rideFilters.startDate, rideFilters.endDate);
    }
    return undefined;
  }, [rideFilters.startDate, rideFilters.endDate]);

  interface BlockUserRequest {
    blockedAccess: string;
    title: {
      fr: string;
      en: string;
      ar: string;
    };
    description: {
      fr: string;
      en: string;
      ar: string;
    };
    until: string;
  }

  const blockOrUnblockUser = async (formData?: BlockUserRequest) => {
    setLoading(true);
    try {
      if (action === "block" && formData) {
        // Block user with form data
        await apiService.MakePutRequest(
          `users/${id}/block-user`,
          formData,
          token,
        );
      } else if (action === "unblock") {
        // Unblock user - pass empty object as data parameter
        await apiService.MakePutRequest(`users/${id}/unblock-user`, {}, token);
      }

      // Refresh the user data after successful operation
      invalidateQueryUserInfoQuery();
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
      // You might want to show an error toast/notification here
    } finally {
      setShowBlockModal(false);
      setShowUnblockModal(false);
      setLoading(false);
    }
  };

  const handleBlockUser = () => {
    setAction("block");
    setShowBlockModal(true);
  };

  const handleUnblockUser = () => {
    setAction("unblock");
    setShowUnblockModal(true);
  };

  return (
    <div className="">
      <BlockUserModal
        open={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        onSubmit={(data) => blockOrUnblockUser(data as BlockUserRequest)}
        loading={loading}
        action={action as "block"}
      />

      <PromptModal
        open={showUnblockModal}
        onAccept={() => blockOrUnblockUser()}
        onClose={() => setShowUnblockModal(false)}
        message={`Voulez-vous vraiment débloquer ce profil ?`}
        loading={loading}
      />

      {/* Tabs */}
      {isPartner ? (
        <PartnerDetailNav
          activeTab={Math.min(activeTab, 3)}
          onTabChange={handleSetActiveTab}
        />
      ) : (
        <UserDetailTabs
          activeTab={activeTab}
          onTabChange={handleSetActiveTab}
          userData={userData!}
        />
      )}

      {/* User Name */}
      <div className="d-flex justify-content-between">
        <UserDetailHeader
          userData={userData}
          blocked={isUserBlocked(userData?.metaData?.blockedUserInfo)}
          loading={loading}
          onBlock={handleBlockUser}
          onUnblock={handleUnblockUser}
        />
      </div>

      {/* Tab Content */}
      {isPartner ? (
        <PartnerDetailContent
          activeTab={Math.min(activeTab, 3)}
          userData={userData!}
          invalidateQuery={invalidateQueryUserInfoQuery}
        />
      ) : (
        <>
          {activeTab === 0 && (
            <GeneralInfoForm
              data={userData}
              role="user"
              invalidateQuery={invalidateQueryUserInfoQuery}
            />
          )}

          {/* Rides */}
          {activeTab === 1 && (
            <RidesTab
              rideFilters={rideFilters}
              setRideFilters={setRideFilters}
              isLoading={isLoading}
              rides={rides}
              handleRidePageChange={(page) =>
                setRideFilters({ ...rideFilters, page })
              }
              setDates={(dates) =>
                setRideFilters((prev) => ({
                  ...prev,
                  startDate: dates.from,
                  endDate: dates.to,
                }))
              }
              defaultPeriod={period}
            />
          )}

          {/* Bookings */}
          {activeTab === 2 && <BookingTab userEmail={userData?.email!} />}

          {/* Booking Reviews */}
          {activeTab === 3 && (
            <BookingReviewsTab
              userBookingReviewsData={userBookingReviewsData}
              isLoading={isLoading}
              BookingReviewsFilters={BookingReviewsFilters}
              handleBookingReviewsPageChange={(page) =>
                setBookingReviewsFilters({ ...BookingReviewsFilters, page })
              }
              handleDeleteReview={deleteReview}
              isDeletingReview={isDeletingReview}
            />
          )}

          {/* Ride Reviews */}
          {activeTab === 4 && (
            <RideReviewsTab
              data={UserRidesreviewsData}
              isLoading={isLoading}
              filters={rideReviewsFilters}
              onPageChange={(page) =>
                setRideReviewsFilters({ ...rideReviewsFilters, page })
              }
              deleteReview={deleteRideReview}
              isDeletingReview={isDeletingRideReview}
            />
          )}

          {/* Balance & Transactions */}
          {activeTab === 5 && (
            <BalanceTab
              userData={userData}
              invalidateQuery={invalidateQueryUserInfoQuery}
            />
          )}

          {/* Points History */}
          {activeTab === 6 && (
            <UserPointsHistoryTab
              data={userPointsHistoryData}
              isLoading={isLoading}
              filters={pointsHistoryFilters}
              setFilters={setPointsHistoryFilters}
              onPageChange={(page) =>
                setPointsHistoryFilters({ ...pointsHistoryFilters, page })
              }
              user={userData!}
            />
          )}
          {activeTab === 7 && <AppConfigurationTab data={userData} />}
        </>
      )}
    </div>
  );
}

function isUserBlocked(userData: UserMetaData["blockedUserInfo"] | undefined) {
  if (isEmpty(userData)) {
    return false;
  }
  if (isNotEmpty(userData?.until) && new Date(userData?.until) < new Date()) {
    return false;
  }
  if (isEmpty(userData?.blockedAccess)) {
    return false;
  }

  return true;
}
